import { apiErrorText } from '@/api/errors';
import { ChipInput } from '@/components/ChipInput';
import { hasOverlappingShifts } from '@/features/opening-hours/lib/shift-overlap';
import { useSetOpeningHours } from '@/features/opening-hours/hooks/useOpeningHours';
import { useErrorT, useT } from '@/i18n';
import type { BulkOpeningHours, OpeningHours } from '@restaurant/api-contracts';
import { BlockTitle, List, ListItem, f7 } from 'framework7-react';
import { useEffect, useMemo, useRef, useState } from 'react';

type ServiceId = BulkOpeningHours['service'];
type Shift = { open: string; close: string };
type DayDraft = { isEnabled: boolean; shifts: Shift[] };
// dayOfWeek is 0=Sun … 6=Sat; operators read the week Monday-first.
type Draft = Record<number, DayDraft>;

export const WEEK: { dow: number; label: string }[] = [
  { dow: 1, label: 'Monday' },
  { dow: 2, label: 'Tuesday' },
  { dow: 3, label: 'Wednesday' },
  { dow: 4, label: 'Thursday' },
  { dow: 5, label: 'Friday' },
  { dow: 6, label: 'Saturday' },
  { dow: 0, label: 'Sunday' },
];

/** dow (0=Sun … 6=Sat) → the day's key in the `openingHours` i18n namespace. */
export const DAY_I18N_KEY: Record<
  number,
  'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday'
> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

export function buildDraft(rows: OpeningHours[], service: ServiceId): Draft {
  const draft: Draft = {};
  for (const { dow } of WEEK) {
    const row = rows.find((r) => r.service === service && r.dayOfWeek === dow);
    draft[dow] = row
      ? { isEnabled: row.isEnabled, shifts: row.shifts.map((s) => ({ ...s })) }
      : { isEnabled: false, shifts: [] };
  }
  return draft;
}

export interface WeeklyHoursEditorState {
  draft: Draft;
  isDirty: boolean;
  isPending: boolean;
  setEnabled: (dow: number, isEnabled: boolean) => void;
  setShift: (dow: number, i: number, key: keyof Shift, value: string) => void;
  addShift: (dow: number) => void;
  removeShift: (dow: number, i: number) => void;
  handleSave: () => void;
}

/**
 * All draft state + save logic for one service's weekly grid. Lives in a hook so
 * the OWNING PAGE can drive both the navbar Save action (SettingsSaveNav) and the
 * grid body from one source of truth - a single bulk PUT replaces the whole
 * service at once.
 */
export function useWeeklyHoursEditor(
  service: ServiceId,
  rows: OpeningHours[],
): WeeklyHoursEditorState {
  const t = useT();
  const errorMessages = useErrorT();
  const save = useSetOpeningHours();
  const [draft, setDraft] = useState<Draft>(() => buildDraft(rows, service));

  const pristine = useMemo(() => buildDraft(rows, service), [rows, service]);

  // Has the operator touched the grid since it was last seeded from the server?
  // Guards the re-seed below so a background refetch - e.g. the one fired right
  // after enabling custom hours, or after a save - can't overwrite edits the
  // operator is still typing. Reset on a tab switch and after a successful save.
  const editedRef = useRef(false);
  const prevServiceRef = useRef(service);

  useEffect(() => {
    // Switching channel tab always loads that channel's schedule (and discards
    // any unsaved edits on the previous tab - same as before).
    if (prevServiceRef.current !== service) {
      prevServiceRef.current = service;
      editedRef.current = false;
      setDraft(buildDraft(rows, service));
      return;
    }
    // Same channel, server data changed (refetch): adopt it only if the operator
    // hasn't started editing - otherwise their in-progress changes win.
    if (!editedRef.current) {
      setDraft(buildDraft(rows, service));
    }
  }, [rows, service]);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(pristine);

  function patchDay(dow: number, next: Partial<DayDraft>) {
    editedRef.current = true;
    setDraft((d) => ({ ...d, [dow]: { ...d[dow], ...next } }));
  }

  function setEnabled(dow: number, isEnabled: boolean) {
    const day = draft[dow];
    // Turning a day on with no hours yet → seed a sensible default shift.
    const shifts =
      isEnabled && day.shifts.length === 0 ? [{ open: '09:00', close: '17:00' }] : day.shifts;
    patchDay(dow, { isEnabled, shifts });
  }

  function setShift(dow: number, i: number, key: keyof Shift, value: string) {
    const shifts = draft[dow].shifts.map((s, idx) => (idx === i ? { ...s, [key]: value } : s));
    patchDay(dow, { shifts });
  }

  function addShift(dow: number) {
    patchDay(dow, { shifts: [...draft[dow].shifts, { open: '17:00', close: '22:00' }] });
  }

  function removeShift(dow: number, i: number) {
    patchDay(dow, { shifts: draft[dow].shifts.filter((_, idx) => idx !== i) });
  }

  // Opening hours (BASE) and the order channels must not open AFTER they close:
  //   - BASE            → the restaurant's day can't end before it starts.
  //   - PICKUP/DELIVERY → serve SAME-DAY slots only, so an overnight window
  //     (close < open, wrapping past midnight) would create slots that belong to
  //     the next day and can't be scheduled.
  // Reservation legitimately runs past midnight, so it alone keeps the wrap.
  const noWrap = service !== 'RESERVATION';

  function handleSave() {
    const invalidTitle = t.openingHours.invalidHoursTitle;
    for (const { dow } of WEEK) {
      const label = t.openingHours[DAY_I18N_KEY[dow]];
      const day = draft[dow];
      if (!day.isEnabled) continue;
      if (day.shifts.length === 0) {
        f7.dialog.alert(t.openingHours.enabledWithoutHours(label), invalidTitle);
        return;
      }
      for (const s of day.shifts) {
        if (!s.open || !s.close || s.open === s.close) {
          f7.dialog.alert(t.openingHours.checkTimes(label), invalidTitle);
          return;
        }
        if (noWrap && s.close < s.open) {
          // BASE frames it as open-after-close; order channels as no-past-midnight.
          const msg =
            service === 'BASE'
              ? t.openingHours.openAfterClose(label)
              : t.openingHours.noOvernightOrders(label);
          f7.dialog.alert(msg, invalidTitle);
          return;
        }
      }
      if (hasOverlappingShifts(day.shifts)) {
        f7.dialog.alert(t.openingHours.shiftsOverlap(label), invalidTitle);
        return;
      }
    }

    const body: BulkOpeningHours = {
      service,
      schedule: WEEK.map(({ dow }) => ({
        dayOfWeek: dow,
        isEnabled: draft[dow].isEnabled,
        shifts: draft[dow].isEnabled ? draft[dow].shifts : [],
      })),
    };

    save.mutate(body, {
      onSuccess: () => {
        // Saved - let the invalidation's refetch re-seed the grid from the
        // persisted rows (so it reflects exactly what the server stored).
        editedRef.current = false;
        toast(t.openingHours.openingSaved);
      },
      onError: (e) => toast(apiErrorText(e, errorMessages, t.openingHours.saveFailed)),
    });
  }

  return {
    draft,
    isDirty,
    isPending: save.isPending,
    setEnabled,
    setShift,
    addShift,
    removeShift,
    handleSave,
  };
}

/**
 * The editable Monday-first weekly grid for one service. Presentational - driven
 * by `useWeeklyHoursEditor` state from the owning page (whose navbar carries the
 * Save action).
 */
export function WeeklyHoursEditor({ editor }: { editor: WeeklyHoursEditorState }) {
  const t = useT();
  const { draft, setEnabled, setShift, addShift, removeShift } = editor;
  return (
    <>
      {WEEK.map(({ dow }) => {
        const day = draft[dow];
        if (!day) return null;
        const label = t.openingHours[DAY_I18N_KEY[dow]];
        return (
          <div key={dow}>
            <BlockTitle>{label}</BlockTitle>
            <List strongIos strongMd insetIos insetMd dividersIos dividersMd>
              <ListItem title={t.openingHours.openLabel}>
                {/* Plain controlled checkbox with F7's `.toggle` markup - the F7
                    <Toggle> component's toggleChange sometimes never wires in this
                    re-rendering master-detail list, so React owns it directly. */}
                <label className="toggle" slot="after">
                  <input
                    type="checkbox"
                    checked={day.isEnabled}
                    onChange={(e) => setEnabled(dow, e.target.checked)}
                  />
                  <span className="toggle-icon" />
                </label>
              </ListItem>
              {day.isEnabled &&
                day.shifts.map((s, i) => (
                  <ListItem
                    key={i}
                    title={
                      day.shifts.length > 1
                        ? t.openingHours.shiftLabel(i + 1)
                        : t.openingHours.hours
                    }
                  >
                    <div slot="after" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ChipInput
                        type="time"
                        value={s.open}
                        step={1}
                        onChange={(v) => setShift(dow, i, 'open', v)}
                        aria-label={t.openingHours.openTimeAria(label)}
                      />
                      <span style={{ color: 'var(--f7-list-item-subtitle-text-color)' }}>–</span>
                      <ChipInput
                        type="time"
                        value={s.close}
                        step={1}
                        onChange={(v) => setShift(dow, i, 'close', v)}
                        aria-label={t.openingHours.closeTimeAria(label)}
                      />
                      <i
                        className="f7-icons"
                        style={{ fontSize: 20, color: '#ff3b30', cursor: 'pointer' }}
                        onClick={() => removeShift(dow, i)}
                        aria-label={t.openingHours.removeHoursAriaLabel}
                      >
                        minus_circle
                      </i>
                    </div>
                  </ListItem>
                ))}
              {day.isEnabled && (
                <ListItem title={t.openingHours.addHours} link="#" onClick={() => addShift(dow)}>
                  <i slot="media" className="f7-icons" style={{ color: 'var(--f7-theme-color)' }}>
                    plus_circle
                  </i>
                </ListItem>
              )}
            </List>
          </div>
        );
      })}
    </>
  );
}
